import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
  appsUserApiRouterSessionView,
  appsUserApiRouterLogoutView,
  appsUserApiRouterGetProfile,
  UserOut
} from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import { useCSRF } from './CSRFContext';

// Custom Session type
export interface Session {
  user: UserOut;
  authenticated: boolean;
}

type AuthContextType = {
  session: Session | null;
  user: UserOut | null;
  profile: UserOut | null;
  loading: boolean;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  checkAuthState: () => Promise<void>;
  // Premium subscription state
  isPremium: boolean;
  subscriptionStatus: string | null;
  capabilities: string[];
  hasCapability: (capability: string) => boolean;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<UserOut | null>(null);
  const [profile, setProfile] = useState<UserOut | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const { ready: csrfReady } = useCSRF();

  // Check auth state after CSRF is ready
  useEffect(() => {
    if (csrfReady) {
      checkAuthState();
    }
  }, [csrfReady]);

  const checkAuthState = async () => {
    try {
      const response = await appsUserApiRouterSessionView();

      if (response.data.authenticated && response.data.user) {
        const sessionData: Session = {
          user: response.data.user,
          authenticated: true,
        };

        setSession(sessionData);
        setUser(response.data.user);
        setProfile(response.data.user);
      } else {
        setSession(null);
        setUser(null);
        setProfile(null);
      }
    } catch (error) {
      console.error('Error checking auth state:', error);
      setSession(null);
      setUser(null);
      setProfile(null);
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    try {
      await appsUserApiRouterLogoutView();
      setSession(null);
      setUser(null);
      setProfile(null);

      toast({
        title: "Signed out successfully",
        description: "You have been signed out of your account",
      });
    } catch (error) {
      console.error('Error signing out:', error);
      toast({
        title: "Sign out failed",
        description: "There was a problem signing you out. Please try again.",
        variant: "destructive",
      });
    }
  };

  const refreshProfile = async () => {
    if (!user) return;

    try {
      const response = await appsUserApiRouterGetProfile();
      const updatedProfile = response.data.user;

      setProfile(updatedProfile);
      setUser(response.data.user);

      if (session) {
        setSession({
          ...session,
          user: response.data.user,
        });
      }
    } catch (error) {
      console.error('Error refreshing profile:', error);
      toast({
        title: "Profile Error",
        description: "There was an issue refreshing your profile. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Premium state derived from user
  const isPremium = user?.is_premium ?? false;
  const subscriptionStatus = user?.subscription_status ?? null;
  const capabilities = user?.capabilities ?? [];

  // Helper to check if user has a specific capability
  const hasCapability = (capability: string): boolean => {
    return capabilities.includes(capability);
  };

  const value = {
    session,
    user,
    profile,
    loading,
    signOut,
    refreshProfile,
    checkAuthState,
    isPremium,
    subscriptionStatus,
    capabilities,
    hasCapability,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
